import { isPendingText, isPendingValue } from "@/utils/isPendingContent";

/** 施術の流れ */
export function FlowSteps({ items }: { items: { no: string; title: string; text: string }[] }) {
  return (
    <ol className="border-t border-foreground-950/12">
      {items.map((step) => (
        <li
          key={step.no}
          className="grid grid-cols-1 gap-3 border-b border-foreground-950/12 py-7 md:grid-cols-[110px_220px_1fr] md:items-baseline md:gap-8 md:py-8"
        >
          <span className="font-label text-[12px] tracking-[0.25em] text-secondary-600">{step.no}</span>
          <h3 className="font-heading text-[17px] text-foreground-950">{step.title}</h3>
          <p className="text-[13px] leading-[2] text-foreground-700 md:text-sm">{step.text}</p>
        </li>
      ))}
    </ol>
  );
}

/** 施術時間・ダウンタイム等 */
export function SpecTable({ items }: { items: { label: string; value: string }[] }) {
  // 未確定（「—」「確認中」「準備中」等）の項目は患者向けに表示しない
  const rows = items.filter((spec) => !isPendingValue(spec.value));
  if (rows.length === 0) return null;
  return (
    <dl className="border-t border-foreground-950/12">
      {rows.map((spec) => (
        <div
          key={spec.label}
          className="grid grid-cols-1 gap-2 border-b border-foreground-950/12 py-6 sm:grid-cols-[240px_1fr] sm:items-baseline sm:gap-8"
        >
          <dt className="text-[13px] text-foreground-600">{spec.label}</dt>
          <dd className="font-heading text-[15px] text-foreground-900 md:text-base">{spec.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * 施術情報（METHOD）。分類・施術方法・使用製剤／使用機器の3項目を、
 * 全施術ページで同一の順序・同一の見せ方に揃えるための専用レイアウト。
 * 罫線と余白とタイポグラフィのみで構成する（カード・影・色付きボックスは使用しない）。
 */
export function MethodTable({ items }: { items: { label: string; value: string }[] }) {
  // 未確定の項目（例：「使用機器：確認中」）は行ごと非表示にする
  const rows = items.filter((item) => !isPendingValue(item.value));
  if (rows.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 border-y border-foreground-950/12 md:grid-cols-3">
      {rows.map((item, index) => (
        <div
          key={item.label}
          className={`flex flex-col gap-2.5 py-6 md:px-8 md:py-8 ${
            index > 0 ? "border-t border-foreground-950/12 md:border-l md:border-t-0" : ""
          } ${index === 0 ? "md:pl-0" : ""} ${index === rows.length - 1 ? "md:pr-0" : ""}`}
        >
          <dt className="text-[12px] tracking-[0.08em] text-foreground-600">{item.label}</dt>
          <dd className="font-heading text-[15px] leading-[1.6] text-foreground-950 md:text-[16px]">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * 施術時間・麻酔・ダウンタイム・推奨回数・推奨間隔（TIME / DOWNTIME）。
 * METHOD と同じ情報階層（小ぶりのラベル＋見出しフォントの値）で全施術ページを統一する。
 * 未確定の「—」は削除せず、確定値より少し控えめなトーンで表示する。
 * 罫線・余白・タイポグラフィのみで構成する（カード・影・色付きボックスは使用しない）。
 */
export function DowntimeTable({ items }: { items: { label: string; value: string }[] }) {
  // 未確定（「—」「確認中」「準備中」等）の行は患者向けに表示しない
  const rows = items.filter((item) => !isPendingValue(item.value));
  if (rows.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 border-t border-foreground-950/12 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((item) => (
        <div
          key={item.label}
          className="flex flex-col gap-2.5 border-b border-foreground-950/12 py-6 sm:py-8 sm:pr-8"
        >
          <dt className="text-[12px] tracking-[0.08em] text-foreground-600">{item.label}</dt>
          <dd className="font-heading text-[15px] leading-[1.6] text-foreground-950 md:text-[16px]">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** リスク・副作用・注意事項 */
export function RiskList({ items, note }: { items: string[]; note?: string }) {
  // 未確定の注意書き（「正式情報を確認のうえ掲載します」等）は表示しない
  const risks = items.filter((risk) => !isPendingText(risk));
  const showNote = !!note && !isPendingText(note);
  if (risks.length === 0 && !showNote) return null;
  return (
    <div>
      <ul className="grid grid-cols-1 gap-x-12 gap-y-4 md:grid-cols-2">
        {risks.map((risk) => (
          <li
            key={risk}
            className="flex items-start gap-3 text-[13px] leading-[2] text-foreground-700 md:text-sm"
          >
            <i className="ri-information-line mt-1 text-accent-700" aria-hidden />
            {risk}
          </li>
        ))}
      </ul>
      {showNote ? (
        <p className="mt-8 border-l-2 border-accent-500 pl-4 text-[12px] leading-[2] text-foreground-600">
          {note}
        </p>
      ) : null}
    </div>
  );
}